import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Paper, Stack, Grid, Switch } from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  createTask,
  ICandidate,
  ICalendarAccount,
  ICalendarEvent,
  IOutlookCalendarEvent,
  IOutlookCalendarAccount,
  ITask,
  updateTask,
  ITaskResponse,
  IJob,
  createCalendarEvent,
  getCalendarAccounts,
  getOutlookCalendarAccounts,
  createOutlookCalendarEvent,
  getJobsFromAdvancedQuery,
  JobAssignmentStatusFilter,
  getJobByJobCode,
  amplitude,
} from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsButtonGroup,
  WsColors,
  WsDateTimePicker,
  WsIconDateOutline,
  WsIconJobOutline,
  WsIconText,
  formattedDateISO,
  WsTypography,
  activityFormattedDate,
  WsSelect,
  WsDialog,
  WsInputNumber,
  WsTypoColors,
  ChatBoxHeader,
  Item,
  WsTypoVariants,
} from 'wisestep-base';
import { object, string } from 'yup';

import { useWorkspaceContext } from '../../../src/context';
import { useSKUModalStore } from '../../components/skuModal/SKUModalStore';
import { getLocation } from '../../features/candidates/utils/CandidateHelper';
import WidgetJobManagement from '../../features/conversationWidget/components/WidgetJobManagement';
import { warning } from '../../utils/NotificationMessages';
import { INotification, useNotificationStore } from '../../utils/NotificationStore';
import { useConversationWidgetStore } from '../../utils/useConversationWidgetStore';
import RecruiterSuggestions from '../filters/components/RecruiterSuggestions';

import { TaskFormValues } from './TaskFormValues';

export const timeArr = ['15mins', '30mins', '1Hr', '2Hr', '1Day', '3Days', '5Days', 'custom'];

export interface WsTaskCreationCardProps {
  candidate?: ICandidate;
  closeDialog: () => void;
  updateTaskProp?: boolean;
  taskDetails?: ITaskResponse;
  selectedJob?: IJob;
  interactionId?: string;
  showHeader?: boolean;
  source?: string;
  handleClose?: (isClose: boolean) => void;
  calendarEventAccountGoogle?: ICalendarAccount;
  calendarEventAccountOutlook?: IOutlookCalendarAccount;
  loadCalendarFromParent?: boolean;
}

const WsTaskCreationCard = ({
  candidate,
  closeDialog,
  updateTaskProp = false,
  taskDetails,
  selectedJob,
  interactionId,
  source,
  showHeader = false,
  handleClose,
  calendarEventAccountGoogle,
  calendarEventAccountOutlook,
  loadCalendarFromParent,
}: WsTaskCreationCardProps) => {
  const validationSchema = {
    title: string().required('This field is required'),
  };
  const methods = useForm<TaskFormValues>({
    defaultValues: {
      title: taskDetails?.title ?? (candidate ? `Creating Reminder for ${candidate.name}` : ''),
      timePeriod: formattedDateISO(dayjs().add(30, 'minute')['$d']),
      reminderIn: 'minutes',
      reminder: taskDetails?.remindBefore,
      description: taskDetails?.description ?? '',
      recruiter: taskDetails?.assignedTo,
    },
    resolver: yupResolver(object().shape(validationSchema)),
  });

  const { handleSubmit, reset, setValue } = methods;
  const workspace = useWorkspaceContext();
  const setModal = useSKUModalStore((state) => state.setModal);
  const activityConversation = useConversationWidgetStore((state) => state.activityConversation);
  const [job, setJob] = useState<IJob | undefined>(selectedJob);
  const [open, setOpen] = useState(false);
  const [openGoogleFailure, setOpenGoogleFailure] = useState(false);
  const [openMicrosoftFailure, setOpenMicrosoftFailure] = useState(false);
  const [googleAccount, setGoogleAccount] = useState<ICalendarAccount | undefined>(
    calendarEventAccountGoogle
  );
  const [outlookCalendarAccount, setOutlookCalendarAccount] = useState<
    IOutlookCalendarAccount | undefined
  >(calendarEventAccountOutlook);
  const [newDueDate, setNewDueDate] = useState('');
  const [googleCalender, setGoogleCalender] = useState(false);
  const [microsoftCalender, setMicrosoftCalender] = useState(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  let timePeriod = '';

  useEffect(() => {
    const jobCode = activityConversation?.jobCode;
    if (jobCode) {
      (async () => {
        const response = await getJobsFromAdvancedQuery({
          jobCodes: [jobCode],
          page: 0,
          size: 2,
          sort: ['scoringData.score,desc'],
          searchAcross: ['MY_TEAMS', 'MY_PERSONAL_DATA'],
          jobAssignmentStatusFilter: JobAssignmentStatusFilter.Everything,
          jobStatus: 'All',
        });

        if (!isEmpty(response.data) && response.data[0]) {
          setJob(response.data[0]);
        }
        if (isEmpty(response.data)) {
          const jobResponse = await getJobByJobCode(jobCode);
          setJob(jobResponse.data);
        }
      })();
    }
  }, [activityConversation]);

  useEffect(() => {
    (async () => {
      const errorObj: INotification = {
        type: 'error',
        message: 'Error in fetching calendar accounts',
      };
      if (!loadCalendarFromParent) {
        try {
          const googleCalendarAcc = await getCalendarAccounts();
          if (googleCalendarAcc?.data && googleCalendarAcc.data.length > 0) {
            setGoogleAccount(googleCalendarAcc.data[0] ?? null);
          }
        } catch (e) {
          setNotification(errorObj);
        }
        try {
          const outlookCalendarAccount = await getOutlookCalendarAccounts();
          if (outlookCalendarAccount?.data && outlookCalendarAccount.data.length > 0) {
            setOutlookCalendarAccount(outlookCalendarAccount.data[0] ?? null);
          }
        } catch (e) {
          setNotification(errorObj);
        }
      }
    })();
  }, [loadCalendarFromParent, setNotification]);

  useEffect(() => {
    const setGoogleOrOutlookAccount = () => {
      if (!isEmpty(googleAccount)) {
        setGoogleCalender(true);
      }
      if (!isEmpty(outlookCalendarAccount)) setMicrosoftCalender(true);
    };
    setGoogleOrOutlookAccount();
  }, [googleAccount, outlookCalendarAccount]);

  const addingTime = {
    ['15mins']: () => {
      const addedDt = dayjs().add(15, 'minute');
      timePeriod = formattedDateISO(addedDt['$d']);
    },
    ['30mins']: () => {
      const addedDt = dayjs().add(30, 'minute');
      timePeriod = formattedDateISO(addedDt['$d']);
    },
    ['1Hr']: () => {
      const addedDt = dayjs().add(1, 'hour');
      timePeriod = formattedDateISO(addedDt['$d']);
    },
    ['2Hr']: () => {
      const addedDt = dayjs().add(2, 'hour');
      timePeriod = formattedDateISO(addedDt['$d']);
    },
    ['1Day']: () => {
      const addedDt = dayjs().add(1, 'day');
      timePeriod = formattedDateISO(addedDt['$d']);
    },
    ['3Days']: () => {
      const addedDt = dayjs().add(3, 'day');
      timePeriod = formattedDateISO(addedDt['$d']);
    },
    ['5Days']: () => {
      const addedDt = dayjs().add(5, 'day');
      timePeriod = formattedDateISO(addedDt['$d']);
    },
  };
  const SelectedIndex = (index: number, value: string) => {
    if (value === 'custom') {
      setOpen(true);
    } else {
      addingTime[value]();
      updateTaskProp && setNewDueDate(timePeriod);
      setValue('timePeriod', timePeriod);
      setOpen(false);
      // handleSubmit(onFormSubmit)();
    }
  };
  const getPayload = () => {
    const payload = {};
    if (candidate) {
      if (!isEmpty(interactionId)) {
        Object.assign(payload, {
          entityType: 'interaction',
          entityId: interactionId,
          attributes: {
            recruiterJobMappingId: candidate?.recruiterJobMappingId,
            candidateEmail: candidate?.email,
            candidateName: candidate?.name,
            candidatePhone: candidate?.phoneNumber,
            jobTitle: job?.title,
            jobCode: job?.jobCode,
            jobId: job?.id,
            jobLocation: job?.locations?.length ? getLocation(job.locations[0]) : null,
            company: job?.companyName,
          },
        });
      } else {
        Object.assign(payload, {
          entityType: 'candidate',
          entityId: candidate?.candidateId ?? candidate?.id,
          attributes: {
            candidateEmail: candidate?.email,
            candidateName: candidate?.name,
            candidatePhone: candidate?.phoneNumber,
            jobTitle: job?.title,
            jobCode: job?.jobCode,
            jobId: job?.id,
            jobLocation: job?.locations?.length ? getLocation(job.locations[0]) : null,
            company: job?.companyName,
          },
        });
      }
    } else {
      Object.assign(payload, { entityType: 'general' });
    }

    return payload;
  };
  const onFormSubmit = async (data: TaskFormValues) => {
    source &&
      amplitude(source, {
        clickedOn: 'Actions',
        pageName: source != 'Talentpool_candidate_Add_reminder' ? 'PipeLine' : 'TalentPool',
        pageUrl: window.location.href,
        workspaceName: workspace.name,
        planType: workspace.planType,
        interfaceType: 'Web',
      });
    if (updateTaskProp) {
      const task: ITask = {
        comment: data.title,
        remindBefore: data.reminderIn === 'minutes' ? data.reminder : data.reminder * 60,
        dueDate: data.customTime ?? data.timePeriod,
        id: taskDetails?.id,
        assignedTo: data.recruiter,
        description: data.description,
      };
      try {
        const response = await updateTask(task);
        if (response.data) {
          addToGoogleCalender(data);
          addToMicrosoftCalender(data);
          setNotification({
            message: 'Task updated successfully.',
            type: 'success',
          });
          closeDialog();
        }
      } catch (error: any) {
        if (error.response.status == 402) {
          setModal({ object: error.response.data, type: 'count' });
        } else {
          setNotification(warning('Something went wrong, Please try again.'));
        }
        closeDialog();
      }
    } else {
      const task: ITask = {
        comment: data.title,
        description: data.description,
        assignedTo: candidate?.recruiterId
          ? Array.from(new Set([candidate.recruiterId].concat(data.recruiter ?? [])))
          : data.recruiter,
        sendEmail: data.sendEmail ?? true,
        remindBefore: data.reminderIn === 'minutes' ? data.reminder : data.reminder * 60,
        dueDate: data.customTime ?? data.timePeriod,
        ...getPayload(),
      };
      try {
        const response = await createTask(task);
        if (response.data) {
          addToGoogleCalender(data);
          addToMicrosoftCalender(data);
          setNotification({
            message: 'Task created successfully.',
            type: 'success',
          });
          closeDialog();
        }
        handleClose && handleClose(false);
      } catch (error: any) {
        if (error.response.status == 402) {
          setModal({ object: error.response.data, type: 'count' });
        } else {
          setNotification(warning('Something went wrong, Please try again.'));
        }
      }
    }
  };

  const sendRemainder = (event: React.ChangeEvent<HTMLInputElement>) =>
    setValue('sendEmail', event.target.checked);

  const checkGoogleCalender = async () => {
    if (!googleCalender && !googleAccount) setOpenGoogleFailure(!googleCalender);
    setGoogleCalender(!googleCalender);
  };

  const checkMicrosoftCalender = async () => {
    if (!microsoftCalender && !outlookCalendarAccount) setOpenMicrosoftFailure(!microsoftCalender);
    setMicrosoftCalender(!microsoftCalender);
  };

  const addToGoogleCalender = async (data: TaskFormValues) => {
    if (googleCalender) {
      let reminder = data.reminder;
      if (data.reminderIn == 'hours') {
        reminder = data.reminder * 60;
      }
      const payload: ICalendarEvent = {
        attendees: data.recruiter ?? [],
        entityId: candidate?.id ?? '',
        entityType: 'interaction',
        eventDescription: data.description,
        eventSummary: data.title,
        from: data.customTime ?? data.timePeriod,
        to: dayjs(data.customTime ?? data.timePeriod)
          .add(15, 'minute')
          .toDate(),
        googleOAuth: googleAccount ?? null,
        location: data.location ?? '',
        reminders: [reminder],
      };
      await createCalendarEvent(payload);
    }
  };

  const addToMicrosoftCalender = async (data: TaskFormValues) => {
    if (microsoftCalender) {
      let reminder = data.reminder;
      if (data.reminderIn == 'hours') {
        reminder = data.reminder * 60;
      }
      const payload: IOutlookCalendarEvent = {
        attendees: data.recruiter,
        entityId: candidate?.id ?? '',
        entityType: 'interaction',
        eventDescription: data.description,
        eventSummary: data.title,
        from: data.customTime ?? data.timePeriod,
        to: dayjs(data.customTime ?? data.timePeriod)
          .add(15, 'minute')
          .toDate(),
        outlookOAuthToken: outlookCalendarAccount ?? null,
        location: data.location ?? '',
        reminders: [reminder],
      };
      await createOutlookCalendarEvent(payload);
    }
  };

  const redirectToGoogleCalenderSettings = () => {
    window.location.href = '/calender-settings';
  };

  const redirectToMicrosoftCalenderSettings = () => {
    window.location.href = '/calender-settings/microsoft';
  };

  const closeGoogleFailure = () => {
    setOpenGoogleFailure(false);
    setGoogleCalender(false);
  };

  const closeMicrosoftFailure = () => {
    setOpenMicrosoftFailure(false);
    setMicrosoftCalender(false);
  };

  const ReminderForm = () => (
    <FormProvider {...methods}>
      <Paper
        variant="outlined"
        sx={{ borderRadius: 2, p: 2 }}
        component="form"
        role="presentation"
        onSubmit={handleSubmit(onFormSubmit)}
        onReset={() => reset()}
      >
        <Grid container>
          {taskDetails?.entityType !== 'notes' && (
            <Grid item xs={12} container alignItems="center" py={0.5}>
              <Grid item xs={3}>
                <WsTypography bold>Title</WsTypography>
              </Grid>
              <Grid item xs={9}>
                <ControlledTextInput
                  name="title"
                  type="text"
                  fullWidth
                  placeholder="Add followup Task"
                  variant="outlined"
                  required
                  disabled={taskDetails?.entityType === 'notes'}
                />
              </Grid>
            </Grid>
          )}
          <Grid item xs={12} container alignItems="center" py={0.5}>
            <Grid item xs={3}>
              <WsTypography bold>Add Guests</WsTypography>
            </Grid>
            <Grid item xs={9}>
              <RecruiterSuggestions
                multiple={true}
                name="recruiter"
                defaultValue={taskDetails?.assignedTo}
                placeholder="Assign to your team members"
              />
            </Grid>
          </Grid>
          {taskDetails?.entityType !== 'notes' && (
            <Grid item xs={12} container alignItems="center" py={0.5}>
              <Grid item xs={3}>
                <WsTypography bold>Description</WsTypography>
              </Grid>
              <Grid item xs={9}>
                <ControlledTextInput
                  name="description"
                  type="text"
                  fullWidth
                  placeholder="Add Description"
                  variant="outlined"
                  multiline
                  disabled={taskDetails?.entityType === 'notes'}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Stack direction="row" py={2}>
          <Box pt={1} pl={0.5}>
            <WsIconDateOutline width={35} height={35} />
          </Box>
          <Grid item>
            <WsButtonGroup
              buttonsList={timeArr}
              inputTitle="Remind me in:*"
              selectedIndexValue={SelectedIndex}
              defaultValue={0}
              size="small"
            />

            {open && (
              <Box mt={2}>
                <WsDateTimePicker name="customTime" label="Custom Date" disablePast={true} />
              </Box>
            )}
          </Grid>
        </Stack>
        {(googleCalender || microsoftCalender) && (
          <>
            <Grid container spacing={2} py={1} alignItems="center">
              <Grid item xs={3}>
                <WsTypography bold>Notify me in</WsTypography>
              </Grid>
              <Grid item xs={5}>
                <WsInputNumber name="reminder" required />
              </Grid>
              <Grid item xs={4}>
                <WsSelect
                  placeholder="Minutes"
                  name="reminderIn"
                  selectArray={[
                    { label: 'Minutes', value: 'minutes' },
                    { label: 'Hours', value: 'hours' },
                  ]}
                  size="small"
                />
              </Grid>
            </Grid>
          </>
        )}
        {updateTaskProp === false && (
          <Box>
            {candidate?.currentJobTravelType ||
              (job?.title && (
                /* <Stack direction="row" spacing={2} pl={2}>
        <Grid item container xs={4} py={1}>
           <WsIconText icon={<WsIconProfile />} text={candidate.recruiterName ?? ''} />
        </Grid> */
                <Grid item xs={4} py={1} pl={2}>
                  <WsIconText
                    icon={<WsIconJobOutline color={WsColors.TheBlues500} />}
                    text={job?.title ?? candidate?.currentJobTitle ?? ''}
                  />
                </Grid>
                /* </Stack> */
              ))}
          </Box>
        )}
        {updateTaskProp && (
          <Box py={1} pl={0.5}>
            <WsTypography>
              {`Current Due Date: ${activityFormattedDate(String(taskDetails?.dueDate ?? ''))}`}
            </WsTypography>
          </Box>
        )}
        {newDueDate && (
          <Box py={1} pl={0.5}>
            <WsTypography>{`New Due Date: ${activityFormattedDate(newDueDate)}`}</WsTypography>
          </Box>
        )}
        {updateTaskProp === false && (
          <>
            <Box py={1} pl={0.5}>
              <WsIconText
                icon={<Switch onChange={sendRemainder} defaultChecked />}
                text={'Notify by email'}
              />
            </Box>
            <Box py={1} pl={0.5}>
              <Stack direction="row" spacing={2}>
                <Grid item container xs={5} py={1}>
                  <WsIconText
                    icon={<Switch onChange={checkGoogleCalender} checked={googleCalender} />}
                    text={'Add to Google Calender'}
                  />
                </Grid>
                <Grid item xs={7} py={1}>
                  {googleAccount && googleCalender ? (
                    <WsTypography color={WsTypoColors.PRIMARY}>{googleAccount.email}</WsTypography>
                  ) : googleAccount ? (
                    <></>
                  ) : (
                    <WsButton
                      size="medium"
                      variant="text"
                      onClick={() => setOpenGoogleFailure(true)}
                    >
                      Configure Now
                    </WsButton>
                  )}
                </Grid>
              </Stack>
              <Stack direction="row" spacing={2}>
                <Grid item container xs={5} py={1}>
                  <WsIconText
                    icon={<Switch onChange={checkMicrosoftCalender} checked={microsoftCalender} />}
                    text={'Add to Microsoft Calender'}
                  />
                </Grid>
                <Grid item xs={7} py={1}>
                  {outlookCalendarAccount && microsoftCalender ? (
                    <WsTypography color={WsTypoColors.PRIMARY}>
                      {outlookCalendarAccount.email}
                    </WsTypography>
                  ) : outlookCalendarAccount ? (
                    <></>
                  ) : (
                    <WsButton
                      size="medium"
                      variant="text"
                      onClick={() => setOpenMicrosoftFailure(true)}
                    >
                      Configure Now
                    </WsButton>
                  )}
                </Grid>
              </Stack>
            </Box>
          </>
        )}

        <Stack spacing={2} direction="row" my={2}>
          {updateTaskProp ? (
            <WsButton size="medium" variant="contained" type="submit">
              Update Reminder
            </WsButton>
          ) : (
            <WsButton size="medium" variant="contained" type="submit">
              Add Reminder
            </WsButton>
          )}
          <WsButton size="medium" variant="text" onClick={closeDialog}>
            Cancel
          </WsButton>
        </Stack>
      </Paper>

      <WsDialog openDialog={openGoogleFailure}>
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }} role="presentation">
          <Box py={1} pl={0.5}>
            <WsTypography>Please configure your email to set up a Google meeting.</WsTypography>
          </Box>
          <Box py={1} pl={0.5}>
            <WsButton size="medium" variant="contained" onClick={redirectToGoogleCalenderSettings}>
              Configure Now
            </WsButton>
            <WsButton size="medium" variant="text" onClick={closeGoogleFailure}>
              Skip
            </WsButton>
          </Box>
        </Paper>
      </WsDialog>
      <WsDialog openDialog={openMicrosoftFailure}>
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }} role="presentation">
          <Box py={1} pl={0.5}>
            <WsTypography>Please configure your email to set up a Microsoft meeting.</WsTypography>
          </Box>
          <Box py={1} pl={0.5}>
            <WsButton
              size="medium"
              variant="contained"
              onClick={redirectToMicrosoftCalenderSettings}
            >
              Configure Now
            </WsButton>
            <WsButton size="medium" variant="text" onClick={closeMicrosoftFailure}>
              Skip
            </WsButton>
          </Box>
        </Paper>
      </WsDialog>
    </FormProvider>
  );

  return (
    <>
      {showHeader ? (
        <>
          <ChatBoxHeader>
            <Grid container>
              <Grid item xs={3}>
                <Box mt={1}>
                  <WsTypography
                    variant={WsTypoVariants.TITLE}
                    color={WsTypoColors.PRIMARY}
                    bold={true}
                  >
                    Setup a Reminder
                  </WsTypography>
                </Box>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ float: 'right' }}>
                  <WidgetJobManagement
                    selectedJob={job}
                    hideIfNoJob={true}
                    jobFetchFailure={!isEmpty(activityConversation) && isEmpty(job)}
                    ss={12}
                    hideRemove={true}
                  />
                </Box>
              </Grid>
            </Grid>
          </ChatBoxHeader>
          <Item sx={{ height: '100vh', overflowY: 'auto' }}>
            <ReminderForm />
          </Item>
        </>
      ) : (
        <Item>
          <ReminderForm />
        </Item>
      )}
    </>
  );
};
export default WsTaskCreationCard;
